<template>
  <div v-if="modelValue" class="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
    <div class="fixed z-10 inset-0 overflow-y-auto">
      <div class="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
        <div class="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <icon name="upload" class="h-5 w-5" />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 id="modal-title" class="text-lg leading-6 font-medium text-gray-900">{{ $t('global.add_files') }}</h3>

                <div class="mt-2">
                  <p class="text-sm text-gray-500">{{ $t('global.add_files_title') }}</p>
                </div>
                <form @submit.prevent="save">
                  <div>
                    <div v-bind="getRootProps()" class="mb-3">
                      <input v-bind="getInputProps()" />
                      <div class="mt-3 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md dropzone cursor-pointer hover:bg-gray-100" :class="(isDragActive) ? 'bg-gray-100' : ''">
                        <div class="space-y-1 text-center">
                          <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <div v-if="!isDragActive" class="flex text-sm text-gray-600">
                            <label for="file-upload" class="relative rounded-md font-medium cursor-pointer text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                              <span>Upload a file</span>
                            </label>
                            <p class="pl-1">or drag and drop</p>
                          </div>
                          <p v-else class="text-sm text-gray-600">Drop the files here ...</p>
                          <p class="text-xs text-gray-500 pb-3">Files up to 2MB</p>
                        </div>
                      </div>
                    </div>

                    <div class="flex flex-wrap">
                      <span v-for="(file, key) in allFiles" :key="key" class="px-4 py-1 mt-1 rounded text-white  bg-gray-400 text-sm bg-transparent text-left mr-2">
                        {{ file.name }}
                        <button type="button" class="bg-transparent hover" @click="deleteFile(key)">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="ml-4" viewBox="0 0 1792 1792">
                            <path d="M1490 1322q0 40-28 68l-136 136q-28 28-68 28t-68-28l-294-294-294 294q-28 28-68 28t-68-28l-136-136q-28-28-28-68t28-68l294-294-294-294q-28-28-28-68t28-68l136-136q28-28 68-28t68 28l294 294 294-294q28-28 68-28t68 28l136 136q28 28 28 68t-28 68l-294 294 294 294q28 28 28 68z" />
                          </svg>
                        </button>
                      </span>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" @click="save">{{ $t('global.upload') }}</button>
            <button type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" @click="$emit('update:modelValue',!modelValue)">{{ $t('global.cancel') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Icon from '@/Shared/Icon'
import { useDropzone } from 'vue3-dropzone'
import {reactive} from 'vue'

export default {
  components: {Icon},
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    uploadUrl: {
      type: String,
      required: true,
    },
    model: {
      type: Object,
    },
  },
  emits: ['update:modelValue'],
  setup() {

    let allFiles = reactive([])

    function onDrop(acceptFiles) {
      allFiles.push(...acceptFiles)
    }

    const { getRootProps, getInputProps, ...rest } = useDropzone({ onDrop })

    return {
      allFiles,
      getRootProps,
      getInputProps,
      ...rest,
    }
  },
  data(){
    return {
      files: [],
      form: this.$inertia.form({
        files: [],
      }),
    }
  },
  methods: {
    async save(){
      this.form.files = this.allFiles
      await this.form.post(this.uploadUrl)
      this.$emit('update:modelValue',!this.modelValue)
      this.form.files = []
      this.allFiles.splice(0)
    },
    deleteFile(key){
      this.allFiles.splice(key, 1)
    },
  },
}
</script>
